import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  IconButton,
  Dialog,
  DialogContent,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { createOrder, updateOrder, getOrderById, getAllCustomers, getAllItems } from '../services/api';
import { toast } from 'react-toastify';
import { styled } from '@mui/system';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import OptimizedSpreadsheet from './spreadsheets/OptimizedSpreadsheet';
import FullScreenLayout from './layouts/FullScreenLayout';
import ProcessingAnimation from './ProcessingAnimation';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommentIcon from '@mui/icons-material/Comment';
import SearchIcon from '@mui/icons-material/Search';
import useSound from 'use-sound';
import OptimizedItemSearch from './common/OptimizedItemSearch';
import { extractSheetData } from '../services/sheetExtractorApi';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import TableChartIcon from '@mui/icons-material/TableChart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import GoogleSheetImport from './common/GoogleSheetImport';

const _SpreadsheetWrapper = styled('div')({
  padding: '1rem',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  width: '100%',
  overflow: 'hidden',
});

const _SpreadsheetTable = styled('table')({
  borderCollapse: 'collapse',
  width: '100%',
  '& th, & td': {
    border: '1px solid #c0c0c0',
    padding: '10px',
    position: 'relative',
  },
  '& th': {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '8px',
    borderBottom: '2px solid #333',
  },
  '& tr:first-child th': {
    padding: '8px',
    borderBottom: '2px solid #333',
  },
  '& tr:nth-child(2) th': {
    padding: '8px',
    borderBottom: '2px solid #333',
    '&:not(:first-child)': {
      borderLeft: '1px solid #c0c0c0',
      borderRight: '1px solid #c0c0c0',
    }
  },
  '& td': {
    backgroundColor: '#fff',
    minWidth: '80px',
    textAlign: 'center',
    padding: 0,
    '&:hover': {
      backgroundColor: '#f8f8f8',
    },
    '&.empty': {
      backgroundColor: '#fafafa',
    },
    '& input': {
      width: '100%',
      height: '100%',
      border: 'none',
      padding: '8px',
      textAlign: 'center',
      backgroundColor: 'transparent',
      '&:focus': {
        outline: '2px solid #2196f3',
        outlineOffset: '-2px',
      },
      '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0
      }
    }
  },
  '& .sticky-corner': {
    borderRight: '2px solid #333',
    borderBottom: '2px solid #333',
    backgroundColor: '#e6e6e6',
    position: 'sticky',
    left: 0,
    zIndex: 2,
  },
});

const _ControlsPanel = styled('div')({
  display: 'flex',
  gap: '1rem',
  padding: '1rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  marginBottom: '1rem',
  flexWrap: 'wrap',
  alignItems: 'center',
});

const _ControlGroup = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  backgroundColor: '#fff',
});

const OrderForm = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    order_number: '',
    status: 'pending',
    notes: '',
    items: []
  });
  const [_activeTab, _setActiveTab] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showProcessingDialog, setShowProcessingDialog] = useState(false);
  const [_searchTerm, setSearchTerm] = useState('');
  const [_searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  const [playSuccess] = useSound('/success.wav', { volume: 0.5 });
  const [playError] = useSound('/error.wav', { volume: 0.5 });
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [_showMethodSelection, setShowMethodSelection] = useState(!orderId);
  const [_importedItems, _setImportedItems] = useState(null);
  const [sheetUrl, setSheetUrl] = useState('');

  const steps = ['Order Details', 'Import Method', 'Items', 'Confirmation'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const customersData = await getAllCustomers();
        setCustomers(customersData.data);

        if (orderId) {
          const orderData = await getOrderById(orderId);
          setFormData(orderData);
        }
      } catch (error) {
        toast.error('Failed to fetch data');
      }
    };

    fetchData();
  }, [orderId]);

  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        const items = await getAllItems();
        const sortedItems = items.sort((a, b) => {
          if (a.type !== b.type) return a.type.localeCompare(b.type);
          if (a.size !== b.size) return parseFloat(a.size) - parseFloat(b.size);
          if (a.brim !== b.brim) return parseFloat(a.brim) - parseFloat(b.brim);
          return a.shape.localeCompare(b.shape);
        });
        setAllItems(sortedItems);
      } catch (error) {
        toast.error('Failed to fetch items');
      }
    };
    fetchAllItems();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddItem = (itemToAdd) => {
    if (!itemToAdd) {
      setFormData({
        ...formData,
        items: [...formData.items, {
          item_id: '',
          quantity: 1,
          status: 'pending',
          notes: ''
        }]
      });
      return;
    }

    const existingItemIndex = formData.items.findIndex(
      item => item.item_id === itemToAdd.id
    );

    if (existingItemIndex !== -1) {
      const newItems = [...formData.items];
      newItems[existingItemIndex] = {
        ...newItems[existingItemIndex],
        quantity: (newItems[existingItemIndex].quantity || 0) + 1
      };
      setFormData({ ...formData, items: newItems });
      playSuccess();
      
      toast.success(`Increased ${itemToAdd.type} - ${itemToAdd.size} quantity`);
    } else {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          item_id: itemToAdd.id,
          quantity: 1,
          status: 'pending',
          notes: ''
        }]
      }));
      playSuccess();
    }

    setSearchTerm('');
    setSearchResults([]);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
      searchInputRef.current.focus();
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setShowProcessingDialog(true);
    
    try {
      const startTime = Date.now();
      
      if (orderId) {
        await updateOrder(orderId, formData);
      } else {
        await createOrder(formData);
      }
      
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 2000 - elapsedTime);
      
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      setShowProcessingDialog(false);
      setShowSuccessDialog(true);
      
      setTimeout(() => {
        setShowSuccessDialog(false);
        navigate('/orders');
      }, 2000);
      
    } catch (error) {
      setShowProcessingDialog(false);
      toast.error('Failed to save order');
      playError();
    } finally {
      setIsProcessing(false);
    }
  };

  const _handleSearch = useCallback((event) => {
    const term = event.target.value.trim();
    setSearchTerm(term);
    
    if (!term) {
      setSearchResults([]);
      return;
    }

    const exactMatch = allItems.find(item => 
      item.barcode === term || 
      (item.alternative_lookups || []).includes(term)
    );

    if (exactMatch) {
      handleAddItem(exactMatch);
      setSearchTerm('');
      setSearchResults([]);
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
        searchInputRef.current.focus();
      }
      return;
    }

    const searchResults = allItems.filter(item => {
      const searchableFields = [
        item.type,
        item.size,
        item.brim,
        item.shape,
        item.barcode,
        ...(item.alternative_lookups || [])
      ].filter(Boolean);

      const itemString = searchableFields.join(' ').toLowerCase();
      const searchTerms = term.toLowerCase().split(' ');

      return searchTerms.every(searchTerm => 
        itemString.includes(searchTerm)
      );
    });

    setSearchResults(searchResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, handleAddItem]);

  const handleNext = () => {
    if (activeStep === 0 && !formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setShowMethodSelection(false);
    setActiveStep(2);
  };

  const renderOrderDetails = () => (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Customer"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            required
          >
            {customers.map((customer) => (
              <MenuItem key={customer.customer_id} value={customer.customer_id}>
                {customer.company_name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Order Number (Optional)"
            name="order_number"
            value={formData.order_number}
            onChange={handleChange}
            helperText="Leave blank for auto-generated number"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const renderMethodSelection = () => (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3 
    }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Choose Order Creation Method
      </Typography>
      
      <Grid container spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            <CardActionArea 
              onClick={() => handleMethodSelect('search')}
              sx={{ height: '100%', p: 2 }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Item Search
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Search or scan items one by one to add to the order
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            <CardActionArea 
              onClick={() => handleMethodSelect('spreadsheet')}
              sx={{ height: '100%', p: 2 }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <TableChartIcon sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Spreadsheet View
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Use an interactive spreadsheet to enter quantities by type
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            <CardActionArea 
              onClick={() => handleMethodSelect('sheet')}
              sx={{ height: '100%', p: 2 }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <InsertLinkIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Import from Sheet
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Import order data directly from a Google Sheet
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSearchView = () => (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'background.default',
      p: 2
    }}>
      <Paper sx={{ 
        p: 3, 
        mb: 2, 
        borderRadius: '16px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Search Items
        </Typography>
        <OptimizedItemSearch
          onItemSelect={(selectedItem) => {
            if (selectedItem) {
              handleAddItem({
                id: selectedItem.id,
                type: selectedItem.type_name,
                size: selectedItem.size_name,
                brim: selectedItem.brim_name,
                shape: selectedItem.shape_name,
                barcode: selectedItem.barcode
              });
            }
          }}
          searchRef={searchInputRef}
          placeholder="Scan barcode or search items..."
          autoFocus={true}
          clearOnSelect={true}
        />
      </Paper>

      <Paper sx={{ 
        flex: 1,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <DataTable
          value={formData.items.map(item => {
            const itemDetails = allItems.find(i => i.id === item.item_id) || {};
            return {
              ...item,
              itemDetails,
              name: `${itemDetails.type || ''} - ${itemDetails.size || ''}`,
              details: `${itemDetails.brim || ''} - ${itemDetails.shape || ''}`
            };
          })}
          dataKey="item_id"
          scrollable 
          scrollHeight="calc(100vh - 400px)"
          responsiveLayout="scroll"
          size="small"
          stripedRows
          showGridlines
          emptyMessage="No items added to order"
        >
          <Column 
            header="Item" 
            body={(rowData) => (
              <Box>
                <Typography variant="subtitle1">
                  {rowData.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rowData.details}
                </Typography>
              </Box>
            )}
            style={{ width: '40%' }}
          />
          <Column 
            header="Quantity" 
            body={(rowData) => (
              <TextField
                type="number"
                value={rowData.quantity}
                onChange={(e) => {
                  const index = formData.items.findIndex(item => item.item_id === rowData.item_id);
                  handleItemChange(index, 'quantity', parseInt(e.target.value) || 0);
                }}
                size="small"
                inputProps={{ min: 0 }}
                sx={{ width: '100px' }}
              />
            )}
            style={{ width: '20%' }}
          />
          <Column 
            header="Status" 
            body={(rowData) => (
              <TextField
                select
                fullWidth
                value={rowData.status}
                onChange={(e) => {
                  const index = formData.items.findIndex(item => item.item_id === rowData.item_id);
                  handleItemChange(index, 'status', e.target.value);
                }}
                size="small"
                sx={{ minWidth: '150px' }}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </TextField>
            )}
            style={{ width: '25%' }}
          />
          <Column 
            header="Notes" 
            body={(rowData) => (
              <IconButton
                size="small"
                onClick={() => {
                  // Handle notes dialog
                }}
              >
                <CommentIcon color={rowData.notes ? 'primary' : 'action'} />
              </IconButton>
            )}
            style={{ width: '7%' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              <IconButton
                color="error"
                onClick={() => {
                  const index = formData.items.findIndex(item => item.item_id === rowData.item_id);
                  handleRemoveItem(index);
                }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
            style={{ width: '8%' }}
          />
        </DataTable>
      </Paper>
    </Box>
  );

  const renderSpreadsheetView = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {formData.items && (
        <OptimizedSpreadsheet
          data={formData.items.reduce((acc, item) => {
            acc[item.item_id] = {
              quantity: item.quantity || 0
            };
            return acc;
          }, {})}
          metrics={['quantity']}
          onCellValueChanged={({ itemId, _metric, newValue }) => {
            const itemIndex = formData.items.findIndex(item => item.item_id === itemId);
            if (itemIndex !== -1) {
              handleItemChange(itemIndex, 'quantity', newValue);
            } else {
              setFormData(prev => ({
                ...prev,
                items: [
                  ...prev.items,
                  {
                    item_id: itemId,
                    quantity: newValue,
                    status: 'pending',
                    notes: ''
                  }
                ]
              }));
            }
          }}
          title="Order Items"
          readOnly={false}
          fullScreen={true}
          hideZeros={true}
        />
      )}
    </Box>
  );

  const renderSheetImport = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <GoogleSheetImport
        sheetUrl={sheetUrl || ''}
        onSheetUrlChange={(url) => setSheetUrl(url)}
        onImport={async (url) => {
          if (!url) {
            toast.error('Please enter a Google Sheet URL');
            return;
          }

          setIsProcessing(true);
          try {
            const result = await extractSheetData(url);
            if (result.success && result.data) {
              const newItems = result.data.map(item => ({
                item_id: item.itemId,
                quantity: item.value,
                status: 'pending',
                notes: ''
              })).filter(item => item.quantity > 0);

              setFormData(prev => ({
                ...prev,
                items: newItems
              }));

              localStorage.setItem('lastUsedSheetUrl', url);
              toast.success(`Successfully imported ${newItems.length} items from sheet`);
              setActiveStep(3);
            }
          } catch (error) {
            toast.error(error.message || 'Failed to extract data from sheet');
            playError();
          } finally {
            setIsProcessing(false);
          }
        }}
        isLoading={isProcessing}
        error={null}
        additionalNotes={["Items not found in the system will be skipped"]}
        sx={{ flex: 1 }}
      />
    </Box>
  );

  const renderConfirmationStep = () => {
    const totalItems = formData.items.length;
    const totalQuantity = formData.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const sheetUrl = localStorage.getItem('lastUsedSheetUrl');
    const showSheetUrl = selectedMethod === 'sheet' && sheetUrl;

    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto', mt: 2 }}>
        <Card>
          <Box sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              >
                <CheckCircleIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              </motion.div>
              <Typography variant="h5" gutterBottom>
                Order Summary
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card className="surface-0 shadow-1">
                  <CardContent>
                    <Typography variant="h6" className="text-xl font-bold mb-3">
                      Customer Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {customers.find(c => c.customer_id === formData.customer_id)?.company_name}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">Order Number</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.order_number || 'Auto-generated'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                        <Tag 
                          severity={
                            formData.status === 'completed' ? 'success' :
                            formData.status === 'processing' ? 'warning' :
                            formData.status === 'cancelled' ? 'danger' : 'info'
                          }
                          value={formData.status.toUpperCase()}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card className="surface-0 shadow-1" sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" className="text-xl font-bold mb-3">
                      Order Statistics
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                        <Typography variant="h4" color="primary.contrastText">
                          {totalItems}
                        </Typography>
                        <Typography variant="body2" color="primary.contrastText">
                          Total Items
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 2 }}>
                        <Typography variant="h4" color="secondary.contrastText">
                          {totalQuantity}
                        </Typography>
                        <Typography variant="body2" color="secondary.contrastText">
                          Total Quantity
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {showSheetUrl && (
                  <Card className="surface-0 shadow-1" sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" className="text-xl font-bold mb-3">
                        Import Source
                      </Typography>
                      <Box sx={{ wordBreak: 'break-all' }}>
                        <Typography variant="subtitle2" color="text.secondary">Google Sheet URL</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {sheetUrl}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Card className="surface-0 shadow-1">
                  <CardContent>
                    <Typography variant="h6" className="text-xl font-bold mb-3">
                      Order Items
                    </Typography>
                    <DataTable 
                      value={formData.items.map(item => {
                        const itemDetails = allItems.find(i => i.id === item.item_id);
                        return {
                          ...item,
                          name: itemDetails ? 
                            `${itemDetails.type} - ${itemDetails.size}${itemDetails.brim ? ` - ${itemDetails.brim}` : ''}${itemDetails.shape ? ` - ${itemDetails.shape}` : ''}` :
                            'Unknown Item'
                        };
                      })} 
                      responsiveLayout="scroll"
                      stripedRows
                      showGridlines
                      size="small"
                      paginator
                      rows={10}
                      rowsPerPageOptions={[5, 10, 20, 50]}
                      emptyMessage="No items added to order"
                      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    >
                      <Column 
                        header="Item" 
                        field="name"
                        sortable
                      />
                      <Column 
                        header="Quantity" 
                        field="quantity"
                        sortable
                        body={(rowData) => (
                          <Tag 
                            value={rowData.quantity}
                            severity={rowData.quantity > 10 ? 'warning' : 'info'}
                          />
                        )}
                      />
                      <Column 
                        header="Status" 
                        field="status"
                        sortable
                        body={(rowData) => (
                          <Tag 
                            severity={
                              rowData.status === 'completed' ? 'success' :
                              rowData.status === 'processing' ? 'warning' :
                              rowData.status === 'cancelled' ? 'danger' : 'info'
                            }
                            value={rowData.status.toUpperCase()}
                          />
                        )}
                      />
                    </DataTable>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Box>
    );
  };

  return (
    <FullScreenLayout
      hasNavigation={true}
      header={
        <Typography variant="h4">
          {orderId ? 'Edit Order' : 'Create New Order'}
        </Typography>
      }
      summary={
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      }
      mainPanel={
        <>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {activeStep === 0 ? (
              renderOrderDetails()
            ) : activeStep === 1 ? (
              renderMethodSelection()
            ) : activeStep === 2 ? (
              selectedMethod === 'sheet' ? renderSheetImport() :
              selectedMethod === 'spreadsheet' ? renderSpreadsheetView() :
              renderSearchView()
            ) : (
              renderConfirmationStep()
            )}

            <Box sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/orders')}
                >
                  Cancel
                </Button>
                
                {activeStep > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (activeStep === 2) {
                        setSelectedMethod(null);
                        setShowMethodSelection(true);
                      }
                      handleBack();
                    }}
                  >
                    Back
                  </Button>
                )}

                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                  >
                    {orderId ? 'Update Order' : 'Create Order'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={activeStep === 0 && !formData.customer_id}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* Processing Animation Dialog */}
          <ProcessingAnimation 
            open={showProcessingDialog} 
            message={orderId ? "Updating Order..." : "Creating Order..."}
          />

          {/* Success Dialog */}
          <Dialog 
            open={showSuccessDialog}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
              }
            }}
          >
            <DialogContent sx={{ textAlign: 'center', py: 6 }}>
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ 
                  type: "spring",
                  stiffness: 260,
                  damping: 20 
                }}
              >
                <CheckCircleIcon 
                  sx={{ 
                    fontSize: 80,
                    color: 'success.main',
                    filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2))'
                  }} 
                />
              </motion.div>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <Typography variant="h5" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                  Success!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Order {orderId ? 'Updated' : 'Created'} Successfully
                </Typography>
              </motion.div>
            </DialogContent>
          </Dialog>
        </>
      }
    />
  );
};

export default OrderForm; 